import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { destroyCookie, parseCookies } from 'nookies'
import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import Logo from '@/assets/logo.png'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthContext } from '@/contexts/AuthContext'
import { useHeadManager } from '@/lib/headManagerProvider'

const SignInSchema = z.object({
  email: z.string().email({ message: 'E-mail inválido' }),
  password: z.string().min(1, { message: 'Senha obrigatória' }),
})

type SignInForm = z.infer<typeof SignInSchema>

export function SignIn() {
  const { setTitle } = useHeadManager()
  const [searchParams] = useSearchParams()
  const { signIn } = useContext(AuthContext)
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<SignInForm>({
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      email: searchParams.get('email') ?? '',
    },
  })

  const [passwordVisible, setPasswordVisible] = useState(false)

  useEffect(() => {
    setTitle('Login')
  }, [setTitle])

  async function handleSignIn(data: SignInForm) {
    try {
      await signIn(data)

      const { redirectAfterLogin: redirectUrl } = parseCookies()
      if (redirectUrl !== undefined) {
        const redirectPath = redirectUrl || '/'
        destroyCookie(null, 'redirectAfterLogin')
        navigate(redirectPath)
      }
    } catch (err) {
      toast.error(
        'Erro ao fazer login. Verifique suas credenciais e tente novamente.',
      )
    }
  }

  function onInvalidSubmit() {
    const messages = Object.values(errors)
      .map((error) => error.message)
      .join(' | ')
    toast.error(`Erro: Campos obrigatórios ausentes - ${messages}`)
  }

  return (
    <div className="p-8">
      <Button variant="ghost" asChild className="absolute top-8 right-8">
        <Link to="/sign-up">Cadastrar-se</Link>
      </Button>

      <div className="flex w-[350px] flex-col justify-center gap-6">
        <div className="flex flex-col gap-2 text-center">
          <img
            src={Logo}
            alt="Logo"
            className="mx-auto mb-2 h-12 w-auto sm:hidden"
          />
          <h1 className="text-2xl font-semibold tracking-tight">
            Entrar na minha conta
          </h1>
          <p className="text-muted-foreground text-sm">
            Acompanhe as melhores rifas, e garanta premiações incríveis!
          </p>
        </div>

        <form
          onSubmit={handleSubmit(handleSignIn, onInvalidSubmit)}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Input
                id="password"
                type={passwordVisible ? 'text' : 'password'}
                {...register('password')}
                className={`${
                  errors.password ? 'border-red-500' : ''
                } bg-white dark:bg-gray-800`}
              />
              <button
                type="button"
                onClick={() => setPasswordVisible((prev) => !prev)}
                className="absolute top-1/2 right-3 -translate-y-1/2 transform text-green-700 dark:text-green-400"
              >
                {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}

            <div className="mt-1 flex justify-end">
              <button
                type="button"
                className="text-sm text-green-600 hover:underline dark:text-green-400"
                onClick={() => {
                  // ação futura
                }}
              >
                Esqueci minha senha
              </button>
            </div>
          </div>

          <Button disabled={isSubmitting} className="w-full" type="submit">
            Entrar
          </Button>
        </form>
      </div>
    </div>
  )
}
